/**
 * Stripe billing helpers for Correly.
 *
 * Setup:
 * 1. Go to stripe.com → Create account
 * 2. Get your Publishable Key from Developers → API Keys
 * 3. Add it to .env as VITE_STRIPE_PUBLISHABLE_KEY
 * 4. Create three products/prices in Stripe Dashboard:
 *    - Starter: $29/month
 *    - Solo: $49/month
 *    - Practice: $99/month
 *    Each with a 14-day free trial
 * 5. Store the price IDs below
 *
 * For the full checkout flow, you'll also need a small server-side function
 * (Supabase Edge Function) to create Stripe Checkout Sessions securely.
 * See the STRIPE_SETUP.md file for details.
 */

// Stripe Price IDs — replace with your actual Stripe price IDs
const PRICE_IDS = {
  starter: import.meta.env.VITE_STRIPE_PRICE_STARTER || 'price_starter_placeholder',
  solo: import.meta.env.VITE_STRIPE_PRICE_SOLO || 'price_solo_placeholder',
  practice: import.meta.env.VITE_STRIPE_PRICE_PRACTICE || 'price_practice_placeholder',
};

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

/**
 * Check if Stripe is configured.
 */
export function isStripeConfigured() {
  return STRIPE_KEY && STRIPE_KEY !== 'pk_test_your-key-here';
}

/**
 * Redirect to Stripe Checkout for a subscription.
 *
 * In production, this should call a server-side endpoint (Supabase Edge Function)
 * that creates the Checkout Session with your Stripe Secret Key.
 *
 * @param {string} tier - 'starter' | 'solo' | 'practice'
 * @param {string} practitionerEmail - email for the customer
 * @param {string} practitionerId - for metadata
 */
export async function createCheckoutSession(tier, practitionerEmail, practitionerId) {
  if (!isStripeConfigured()) {
    console.warn('Stripe not configured. Add VITE_STRIPE_PUBLISHABLE_KEY to .env');
    return { error: 'Stripe is not configured yet. Add your Stripe credentials to .env to enable billing.' };
  }

  const priceId = PRICE_IDS[tier];
  if (!priceId || priceId.includes('placeholder')) {
    return { error: 'Stripe price IDs not configured. Create products in your Stripe Dashboard first.' };
  }

  try {
    // In production, call your Supabase Edge Function:
    // const response = await fetch('/api/create-checkout-session', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     priceId,
    //     customerEmail: practitionerEmail,
    //     practitionerId,
    //     successUrl: window.location.origin + '/practitioner/settings?billing=success',
    //     cancelUrl: window.location.origin + '/practitioner/settings?billing=cancelled',
    //   }),
    // });
    // const { url } = await response.json();
    // window.location.href = url;

    return {
      error: null,
      message: `Stripe Checkout would redirect to ${tier} plan ($${tier === 'starter' ? 29 : tier === 'solo' ? 49 : 99}/month). Configure Stripe Edge Function to enable.`,
    };
  } catch (err) {
    return { error: err.message };
  }
}

/**
 * Redirect to Stripe Customer Portal for managing subscription.
 * Requires a server-side endpoint to create the portal session.
 */
export async function openCustomerPortal(stripeCustomerId) {
  if (!isStripeConfigured()) {
    return { error: 'Stripe not configured.' };
  }

  try {
    // In production:
    // const response = await fetch('/api/create-portal-session', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     customerId: stripeCustomerId,
    //     returnUrl: window.location.origin + '/practitioner/settings',
    //   }),
    // });
    // const { url } = await response.json();
    // window.location.href = url;

    return { error: null, message: 'Customer portal would open. Configure Stripe Edge Function to enable.' };
  } catch (err) {
    return { error: err.message };
  }
}
